import { v4 as uuid } from 'uuid';
import { IEntityContentJson, ModuleTypes } from '@beefree.io/sdk/dist/types/bee';
import { RRule, Weekday, ByWeekday } from 'rrule';
import { DateTime } from 'luxon';

import { TemplateTransactionType } from '@/bundles/MarketingTemplate/types';
import { ITextValue } from '@/types/common';
import { Nullable } from '@/utils/types';

export * from './headers';
export * from './customRows';

export const getUnsubscribeRow = () => ({
  descriptor: {
    computedStyle: {
      hideContentOnAmp: false,
        hideContentOnDesktop: false,
        hideContentOnHtml: false,
        hideContentOnMobile: false
    },
    html: {
      html: "\u003Ca href=\"{{unsubscribe_link}}\" style=\"text-decoration:none;display:inline-block;color:#B4B4B4;background-color:transparent;border-radius:4px;width:auto;border-top:0px solid transparent;font-weight:400;border-right:0px solid transparent;border-bottom:0px solid transparent;border-left:0px solid transparent;padding-top:0px;padding-bottom:0px;font-family:Arial,'Helvetica Neue',Helvetica,sans-serif;font-size:10px;text-align:center;word-break:keep-all\" target=\"_blank\"\u003E\n  \u003Cspan style=\"padding-left:0px;padding-right:0px;font-size:14px;display:inline-block;letter-spacing:normal\"\u003E\n  \t\u003Cspan style=\"white-space: nowrap;line-height:20px\"\u003Eunsubscribe\u003C/span\u003E\n  \u003C/span\u003E\n\u003C/a\u003E"
    },
    style: {
      'padding-bottom': '0px',
        'padding-left': '0px',
        'padding-right': '0px',
        'padding-top': '0px'
    }
  },
  type: ModuleTypes.HTML,
  locked: true,
  uuid: uuid(),
});

export const getEmptyTemplate = (): IEntityContentJson => {
  return {
    page: {
      title: 'Template Base',
      description: 'Test template for BEE',
      template: {
        name: 'template-base',
        type: 'basic',
        version: '0.0.1'
      },
      body: {
        type: 'mailup-bee-page-proprerties',
        container: {
          style: {
            'background-color': '#FFFFFF'
          }
        },
        content: {
          style: {
            'font-family': "Arial, 'Helvetica Neue', Helvetica, sans-serif",
            color: '#000000'
          },
          computedStyle: {
            linkColor: '#0068A5',
            messageBackgroundColor: 'transparent',
            messageWidth: '500px',
            align: 'center'
          }
        },
        webFonts: []
      },
      rows: [
        {
          type: 'one-column-empty',
          container: {
            style: {
              'background-color': 'transparent',
              'background-image': 'none',
              'background-repeat': 'no-repeat',
              'background-position': 'top left'
            }
          },
          content: {
            style: {
              'background-color': 'transparent',
              color: '#000000',
              width: '500px',
              'background-image': 'none',
              'background-repeat': 'no-repeat',
              'background-position': 'top left'
            },
            computedStyle: {
              rowColStackOnMobile: true,
              rowReverseColStackOnMobile: false
            }
          },
          columns: [
            {
              'grid-columns': 12,
              // ts ignore is needed coz not sure why it's not possible to have html block in modules by ts types (but it's possible to add it over editor)
              // @ts-ignore
              modules: [getUnsubscribeRow()],
              style: {
                'background-color': 'transparent',
                'padding-top': '5px',
                'padding-right': '0px',
                'padding-bottom': '5px',
                'padding-left': '0px',
                'border-top': '0px solid transparent',
                'border-right': '0px solid transparent',
                'border-bottom': '0px solid transparent',
                'border-left': '0px solid transparent'
              },
              uuid: uuid()
            }
          ],
          uuid: uuid()
        }
      ]
    }
  };
};

export const getEmptyHtmlString = () => {
  return '<!DOCTYPE html><html><head></head><body></body></html>';
};

export function getTransactionTypeLabels () {
  return [
    { text: 'For Sale', value: TemplateTransactionType.for_sale },
    { text: 'For Lease', value: TemplateTransactionType.for_lease },
    { text: 'Sublease', value: TemplateTransactionType.sublease },
    { text: 'Investment Sale', value: TemplateTransactionType.investment_sale },
  ];
}
export const getDaysOfWeekItems = (): ITextValue<Weekday>[] => [
  { text: 'Monday', value: RRule.MO },
  { text: 'Tuesday', value: RRule.TU },
  { text: 'Wednesday', value: RRule.WE },
  { text: 'Thursday', value: RRule.TH },
  { text: 'Friday', value: RRule.FR },
  { text: 'Saturday', value: RRule.SA },
  { text: 'Sunday', value: RRule.SU },
];

export const getTimeItems = (dateStart: Nullable<string>, interval = 15): ITextValue<string>[] => {
  const options: ITextValue<string>[] = [];
  const isStartToday = dateStart === DateTime.now().toISODate();
  // 10 min lead keeps the first slot ahead of "now" by the time the form is saved
  let time = isStartToday ? DateTime.now().plus({ minutes: 10 }).startOf('minute') : DateTime.now().startOf('day');
  const endAt = DateTime.now().set({ hour: 23, minute: 46, second: 0, millisecond: 0 });

  const minuteRemainder = (time.hour * 60 + time.minute) % interval;
  if (minuteRemainder !== 0) {
    time = time.plus({ minutes: interval - minuteRemainder });
  }

  while (time <= endAt) {
    options.push({
      text: time.toLocaleString(DateTime.TIME_SIMPLE),
      value: time.toLocaleString(DateTime.TIME_24_SIMPLE),
    });

    time = time.plus({ minutes: interval });
  }

  return options;
}

export const getBysetposItems = (): ITextValue<number>[] => [
  { text: 'First', value: 1 },
  { text: 'Second', value: 2 },
  { text: 'Third', value: 3 },
  { text: 'Fourth', value: 4 },
  { text: 'Fifth', value: 5 },
];

const WEEKDAY_BY_NUMBER: Record<number, Weekday> = {
  [RRule.MO.weekday]: RRule.MO,
  [RRule.TU.weekday]: RRule.TU,
  [RRule.WE.weekday]: RRule.WE,
  [RRule.TH.weekday]: RRule.TH,
  [RRule.FR.weekday]: RRule.FR,
  [RRule.SA.weekday]: RRule.SA,
  [RRule.SU.weekday]: RRule.SU,
};

export const parseWeekday = (byweekday?: Nullable<ByWeekday | ByWeekday[]>): Weekday => {
  const value = Array.isArray(byweekday) ? byweekday[0] : byweekday;

  if (value instanceof Weekday) {
    return WEEKDAY_BY_NUMBER[value.weekday] || RRule.MO;
  }

  if (typeof value === 'number') {
    return WEEKDAY_BY_NUMBER[value] || RRule.MO;
  }

  if (typeof value === 'string') {
    return WEEKDAY_BY_NUMBER[Weekday.fromStr(value).weekday] || RRule.MO;
  }

  return RRule.MO;
};
